import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fetchLeads, updateLeadStatus, updateLeadStage, deleteLead } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Trash2, RefreshCw, LogOut } from "lucide-react";

const STATUSES = ["New", "Contacted", "Closed"];
const STAGES = ["Consultation", "Design", "Manufacturing", "Installation", "Handover"];

const badge = (s) =>
  s === "New"
    ? "bg-[#1C1A17] text-white"
    : s === "Contacted"
    ? "bg-amber-100 text-amber-800"
    : "bg-emerald-100 text-emerald-800";

export default function AdminDashboard() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      setLeads(await fetchLeads());
    } catch {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const changeStatus = async (id, status) => {
    try {
      const updated = await updateLeadStatus(id, status);
      setLeads((ls) => ls.map((l) => (l.id === id ? updated : l)));
      toast.success(`Marked as ${status}`);
    } catch { toast.error("Update failed"); }
  };

  const changeStage = async (id, stage) => {
    try {
      const updated = await updateLeadStage(id, stage);
      setLeads((ls) => ls.map((l) => (l.id === id ? updated : l)));
      toast.success(`Stage: ${stage}`);
    } catch { toast.error("Stage update failed"); }
  };

  const remove = async (id) => {
    try {
      await deleteLead(id);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success("Lead deleted");
    } catch { toast.error("Delete failed"); }
  };

  const handleLogout = async () => {
    await signOut();
    navigate("/", { replace: true });
  };

  const shown = useMemo(() => leads.filter((l) => {
    const okStatus = filter === "All" || l.status === filter;
    const q = search.toLowerCase();
    const okSearch = !q || l.name.toLowerCase().includes(q) || l.email.toLowerCase().includes(q) || (l.city || "").toLowerCase().includes(q);
    return okStatus && okSearch;
  }), [leads, filter, search]);

  const counts = useMemo(() => ({
    total: leads.length,
    New: leads.filter((l) => l.status === "New").length,
    Contacted: leads.filter((l) => l.status === "Contacted").length,
    Closed: leads.filter((l) => l.status === "Closed").length,
  }), [leads]);

  return (
    <div className="min-h-screen bg-[#F5F2ED]">
      <header className="bg-white border-b border-black/5 sticky top-0 z-30">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="font-head text-xl font-extrabold tracking-tight">
            SPHERIO LIVING<span className="text-[#1C1A17]">.</span>{" "}
            <span className="text-sm font-sans font-medium text-[#5C574F]">Admin</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/admin/blog"
              data-testid="admin-blog-nav-link"
              className="text-sm text-[#5C574F] hover:text-[#1C1A17]"
            >
              Blog
            </Link>
            {user?.picture && (
              <img src={user.picture} alt={user.name} className="h-8 w-8 rounded-full object-cover" />
            )}
            <div className="hidden sm:block text-sm">
              <div className="font-medium leading-tight" data-testid="admin-user-name">{user?.name}</div>
              <div className="text-[#5C574F] text-xs">{user?.email}</div>
            </div>
            <button data-testid="admin-refresh" onClick={load} className="inline-flex items-center gap-2 text-sm text-[#5C574F] hover:text-[#1C1A17]">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button data-testid="admin-logout" onClick={handleLogout} className="inline-flex items-center gap-2 text-sm text-[#5C574F] hover:text-[#1C1A17]">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10">
        <h1 className="font-head text-3xl sm:text-4xl font-medium tracking-tight">Leads</h1>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { l: "Total", v: counts.total },
            { l: "New", v: counts.New },
            { l: "Contacted", v: counts.Contacted },
            { l: "Closed", v: counts.Closed },
          ].map((s) => (
            <div key={s.l} className="rounded-2xl bg-white border border-black/10 p-5">
              <div className="font-head text-3xl font-semibold text-[#1C1A17]">{s.v}</div>
              <div className="text-sm text-[#5C574F] mt-1">{s.l}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-4 sm:items-center">
          <Input
            data-testid="admin-search"
            placeholder="Search name, email, city..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-11 rounded-xl bg-white max-w-xs"
          />
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-11 rounded-xl bg-white w-44" data-testid="admin-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {["All", ...STATUSES].map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        <div className="mt-6 overflow-x-auto rounded-2xl border border-black/10 bg-white">
          <table className="w-full text-sm" data-testid="admin-leads-table">
            <thead>
              <tr className="text-left text-[#5C574F] border-b border-black/10">
                <th className="p-4 font-medium">Name</th>
                <th className="p-4 font-medium">Contact</th>
                <th className="p-4 font-medium">City</th>
                <th className="p-4 font-medium">Service</th>
                <th className="p-4 font-medium">Source</th>
                <th className="p-4 font-medium">Status</th>
                <th className="p-4 font-medium">Stage</th>
                <th className="p-4 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 && (<tr><td colSpan={8} className="p-10 text-center text-[#5C574F]">No leads found.</td></tr>)}
              {shown.map((l) => (
                <tr key={l.id} className="border-b border-black/5 align-top hover:bg-[#F5F2ED]" data-testid={`lead-row-${l.id}`}>
                  <td className="p-4">
                    <div className="font-medium text-[#0A0A0A]">{l.name}</div>
                    {l.message && <div className="text-xs text-[#5C574F] mt-1 max-w-[220px]">{l.message}</div>}
                  </td>
                  <td className="p-4 text-[#5C574F]"><div>{l.email}</div><div>{l.phone}</div></td>
                  <td className="p-4 text-[#5C574F]">{l.city || "-"}</td>
                  <td className="p-4 text-[#5C574F]">{l.service || "-"}</td>
                  <td className="p-4"><span className="capitalize text-[#5C574F]">{l.source}</span></td>
                  <td className="p-4">
                    <Select value={l.status} onValueChange={(v) => changeStatus(l.id, v)}>
                      <SelectTrigger className={`h-9 rounded-full w-36 border-none text-xs font-semibold ${badge(l.status)}`} data-testid={`status-${l.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-4">
                    <Select value={l.stage || "Consultation"} onValueChange={(v) => changeStage(l.id, v)}>
                      <SelectTrigger className="h-9 rounded-full w-40 border border-[#1C1A17]/30 bg-[#1C1A17]/5 text-[#1C1A17] text-xs font-semibold" data-testid={`stage-${l.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STAGES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-4">
                    <button data-testid={`delete-${l.id}`} onClick={() => remove(l.id)} className="text-[#5C574F] hover:text-red-600 transition-colors">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
      <Toaster position="top-center" richColors />
    </div>
  );
}
